/**
 * Production Central API Client Helper
 * Provides strict response validation:
 * - Validates response.ok
 * - Strictly inspects Content-Type (application/json)
 * - Detects and prevents HTML / SPA fallback index.html parsing errors ("Unexpected token '<'")
 * - Returns structured error messages without crashing
 */

export interface ApiFetchOptions extends RequestInit {
  timeoutMs?: number;
  skipJsonParse?: boolean;
}

export class ApiError extends Error {
  public status: number;
  public code?: string;
  public details?: any;
  public isHtmlFallback: boolean;

  constructor(message: string, status: number, code?: string, details?: any, isHtmlFallback = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.isHtmlFallback = isHtmlFallback;
  }
}

export async function apiFetch<T = any>(url: string, options: ApiFetchOptions = {}): Promise<T> {
  const { timeoutMs = 25000, skipJsonParse = false, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const headers = new Headers(fetchOptions.headers || {});
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    const res = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    const isJson = contentType.includes('application/json');

    // If response returned HTML
    if (!isJson) {
      const rawText = await res.text();
      const isHtml =
        rawText.trim().startsWith('<') ||
        rawText.includes('<!doctype html') ||
        rawText.includes('<html') ||
        rawText.includes('id="root"');

      if (isHtml) {
        console.error(`[apiFetch] API routing error at ${url}. Received HTML instead of JSON:`, rawText.slice(0, 200));
        throw new ApiError(
          'API yapılandırma hatası: Sunucu JSON yerine sayfa yanıtı (HTML) döndürdü. Lütfen API yönlendirmesini kontrol ediniz.',
          res.status,
          'API_HTML_FALLBACK_ERROR',
          { url, status: res.status },
          true
        );
      }

      if (!res.ok) {
        throw new ApiError(
          `Sunucu hatası (HTTP ${res.status}): ${rawText.slice(0, 150)}`,
          res.status,
          `HTTP_${res.status}`,
          rawText
        );
      }

      if (skipJsonParse) {
        return rawText as unknown as T;
      }
    }

    // Parse JSON
    let data: any;
    try {
      data = await res.json();
    } catch (parseErr: any) {
      throw new ApiError(
        'Sunucu yanıtı JSON olarak ayrıştırılamadı.',
        res.status,
        'JSON_PARSE_ERROR',
        parseErr?.message
      );
    }

    if (!res.ok) {
      const errMsg = data?.error || data?.message || `İşlem gerçekleştirilemedi (HTTP ${res.status}).`;
      throw new ApiError(errMsg, res.status, data?.code || `HTTP_${res.status}`, data);
    }

    if (data && typeof data === 'object' && data.success === false) {
      const errMsg = data.error || data.message || 'İşlem başarısız oldu.';
      throw new ApiError(errMsg, res.status, data.code || 'BUSINESS_ERROR', data);
    }

    return data as T;
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err instanceof ApiError) {
      throw err;
    }
    if (err.name === 'AbortError') {
      throw new ApiError('İstek zaman aşımına uğradı. Lütfen internet bağlantınızı kontrol edip tekrar deneyiniz.', 408, 'REQUEST_TIMEOUT');
    }
    throw new ApiError(err?.message || 'Ağ bağlantı hatası meydana geldi.', 0, 'NETWORK_ERROR');
  }
}
